import { PLAY_API_GLOBAL, PLAY_EVENT_NAME, buildPlayDataMessage } from "./protocol.js";
const DEFAULT_POLL_INTERVAL_MS = 100;
const DEFAULT_READY_TIMEOUT_MS = 45_000;
/**
 * How long to wait for the player's own `ready`/`loaded` announcement before
 * force-feeding a queued payload through the API anyway. Loading the instant
 * the API global appears races the app's internal boot (its required-font
 * ensure in particular), which permanently breaks the story window's first
 * refresh — so the announcement gate is the primary path and this is only a
 * safety valve for player builds that never announce.
 */
const DEFAULT_ANNOUNCE_GRACE_MS = 15_000;
/**
 * Create a control handle over an embedded standalone player. This is the single
 * shared entry point every preview surface uses so "preview === product" holds
 * by construction: the surface hosts the real Cocos renderer rather than a
 * reimplementation of it.
 *
 * Readiness is handled transparently — `load()` may be called immediately after
 * mounting the iframe; the payload is queued and flushed once the player boots.
 */
export function createEmbeddedPlayerHost(options) {
    const { iframe, onEvent, hostWindow = globalThis, playerOrigin = "*", pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, readyTimeoutMs = DEFAULT_READY_TIMEOUT_MS, } = options;
    const resolveApi = options.resolveApi ?? (() => readInjectedApi(iframe));
    let api = null;
    let disposed = false;
    let pollTimer = null;
    let timeoutTimer = null;
    let pendingLoad = null;
    // The player announces boot completion with a `ready` (awaiting data) or
    // `loaded` (boot-channel data consumed) event. Feeding `api.load()` before
    // that races the app's internal boot, so direct loads gate on it.
    let playerAnnounced = false;
    let announceGraceTimer = null;
    const readyWaiters = [];
    const flushPendingToApi = () => {
        if (disposed || !api || pendingLoad === null)
            return;
        const data = pendingLoad;
        pendingLoad = null;
        safeInvoke(() => api.load(data));
    };
    const noteAnnouncement = () => {
        if (playerAnnounced)
            return;
        playerAnnounced = true;
        if (announceGraceTimer !== null) {
            clearTimeout(announceGraceTimer);
            announceGraceTimer = null;
        }
        flushPendingToApi();
    };
    const emit = (event) => {
        if (event.event === "ready" || event.event === "loaded") {
            noteAnnouncement();
        }
        if (onEvent)
            onEvent(event);
    };
    // Same-origin: the player dispatches its events on its own window.
    const handleContentEvent = (evt) => {
        const detail = evt.detail;
        if (detail && typeof detail.event !== "undefined")
            emit(detail);
    };
    // Cross-origin fallback: the player (or a shim) may forward events via postMessage.
    const handleHostMessage = (evt) => {
        const data = evt.data;
        if (!data || data.type !== PLAY_EVENT_NAME)
            return;
        if (isPlayEvent(data.detail)) {
            emit(data.detail);
            return;
        }
        if (typeof data.event !== "undefined") {
            emit(data);
        }
    };
    const stopWaiting = () => {
        if (pollTimer !== null) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
        if (timeoutTimer !== null) {
            clearTimeout(timeoutTimer);
            timeoutTimer = null;
        }
    };
    const becomeReady = (resolved) => {
        if (disposed || api)
            return;
        api = resolved;
        stopWaiting();
        if (playerAnnounced) {
            flushPendingToApi();
        }
        else if (pendingLoad !== null && announceGraceTimer === null) {
            // Safety valve: if this player build never announces, load anyway after
            // a grace period rather than sitting on the payload forever.
            announceGraceTimer = setTimeout(() => {
                announceGraceTimer = null;
                flushPendingToApi();
            }, DEFAULT_ANNOUNCE_GRACE_MS);
        }
        for (const waiter of readyWaiters.splice(0))
            waiter.resolve();
    };
    const postPending = () => {
        if (pendingLoad === null)
            return;
        safeInvoke(() => iframe.contentWindow?.postMessage(buildPlayDataMessage(pendingLoad), playerOrigin));
    };
    let repostTick = 0;
    const tryResolve = () => {
        if (disposed || api)
            return;
        const found = resolveApi();
        if (found) {
            becomeReady(found);
            return;
        }
        // A single early postMessage is lost if the player hasn't booted yet, and a
        // cross-origin / webview player never exposes its API to our poll — so keep
        // re-posting the queued payload (throttled) until the player picks it up.
        if (pendingLoad !== null && repostTick++ % 5 === 0) {
            postPending();
        }
    };
    // Attach the same-origin content listener where possible; always attach the
    // host message listener for the cross-origin path.
    let contentWindow = null;
    try {
        contentWindow = iframe.contentWindow;
        contentWindow?.addEventListener(PLAY_EVENT_NAME, handleContentEvent);
    }
    catch {
        contentWindow = null; // cross-origin: direct attachment is blocked
    }
    hostWindow.addEventListener("message", handleHostMessage);
    tryResolve();
    if (!api) {
        pollTimer = setInterval(tryResolve, pollIntervalMs);
        timeoutTimer = setTimeout(() => {
            stopWaiting();
            // Only reject internal ready() waiters. We deliberately do NOT emit an
            // "error" event here: a same-origin webview player is driven purely by
            // postMessage and never exposes its API to our poll, so a timeout is
            // expected and must not surface as a user-facing "failed to load" banner.
            const error = new Error("embedded-player: standalone player API not detected before timeout");
            for (const waiter of readyWaiters.splice(0))
                waiter.reject(error);
        }, readyTimeoutMs);
    }
    return {
        load(data) {
            if (disposed)
                return;
            if (api && playerAnnounced) {
                safeInvoke(() => api.load(data));
                return;
            }
            pendingLoad = data; // flushed once the player announces (or grace expires)
            if (api) {
                if (announceGraceTimer === null) {
                    announceGraceTimer = setTimeout(() => {
                        announceGraceTimer = null;
                        flushPendingToApi();
                    }, DEFAULT_ANNOUNCE_GRACE_MS);
                }
                return;
            }
            // Fire the documented postMessage injection immediately; the poll loop
            // keeps re-posting until the player boots and picks it up.
            postPending();
        },
        continue() {
            if (!disposed && api)
                safeInvoke(() => api.continue());
        },
        selectChoice(index) {
            if (!disposed && api)
                safeInvoke(() => api.selectChoice(index));
        },
        getState() {
            return !disposed && api ? safeCall(() => api.getState()) : undefined;
        },
        get isReady() {
            return !disposed && api !== null;
        },
        ready() {
            if (api)
                return Promise.resolve();
            if (disposed)
                return Promise.reject(new Error("embedded-player: host disposed"));
            return new Promise((resolve, reject) => readyWaiters.push({ resolve, reject }));
        },
        dispose() {
            if (disposed)
                return;
            disposed = true;
            stopWaiting();
            if (announceGraceTimer !== null) {
                clearTimeout(announceGraceTimer);
                announceGraceTimer = null;
            }
            try {
                contentWindow?.removeEventListener(PLAY_EVENT_NAME, handleContentEvent);
            }
            catch {
                /* cross-origin teardown is a no-op */
            }
            hostWindow.removeEventListener("message", handleHostMessage);
            for (const waiter of readyWaiters.splice(0)) {
                waiter.reject(new Error("embedded-player: host disposed"));
            }
        },
    };
}
function readInjectedApi(iframe) {
    try {
        const win = iframe.contentWindow;
        if (!win)
            return null;
        const api = win[PLAY_API_GLOBAL];
        return api && typeof api.load === "function" ? api : null;
    }
    catch {
        return null; // cross-origin access throws a SecurityError
    }
}
function isPlayEvent(value) {
    return (typeof value === "object" &&
        value !== null &&
        typeof value.event !== "undefined");
}
function safeInvoke(fn) {
    try {
        fn();
    }
    catch {
        /* the player surfaces its own error event; never let a call throw into the host */
    }
}
function safeCall(fn) {
    try {
        return fn();
    }
    catch {
        return undefined;
    }
}
